import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"
import UsageStatisticsSection from "../../organisms/charts/UsageStatisticsSection"
import ProgressBar from "../ProgressBar"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { TERMINAL_MAX_WARNING, getTerminalGroupsText } from "../../../utils/design-helpers"
import { getPercentage, mbGbDisplay } from "../../../utils/helpers"
import Card from "../../atoms/Card"
import CardBar from "../../atoms/CardBar"
import TerminalStatusCircle from "../../atoms/TerminalStatusCircle"

import H3 from "../../atoms/typography/H3"
import H4 from "../../atoms/typography/H4"
import TextButton from "../../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../../atoms/icons/ArrowDropUpIcon"
import AddDataSection from "../AddDataSection"
import { TerminalDataModel } from "../../../utils/models"
import AddDataButton from "../../atoms/buttons/AddDataButton"
import BodyLarge from "../../atoms/typography/BodyLarge"
import Body from "../../atoms/typography/Body"

interface Props {
  terminal: TerminalDataModel
  useGreyBackground: boolean
  onClickTerminalDetails: () => void
  cardOpen: boolean
}

const BarContentContainer = styled.div<{ greybg?: boolean }>`
  background-color: ${(props) => (props.greybg ? props.theme.colors.greyLighter : props.theme.colors.white)};
  padding: ${(props) => props.theme.spacings.large};

  width: 100%;

  :hover .poop {
    display: block;
  }
`

const ProductImage = styled.img`
  width: 35px;
  height: 35px;
  object-fit: cover;
`

const TerminalOverviewMobileview: React.FunctionComponent<Props> = ({
  terminal,
  useGreyBackground,
  onClickTerminalDetails,
  cardOpen,
}) => {
  const progressPercentage =
    (terminal.dataUsage && getPercentage(terminal.dataUsage.used, terminal.dataUsage.total)) ?? 0

  const [addDataOpen, setAddDataOpen] = React.useState(false)
  const [detailsOpen, setDetailsOpen] = React.useState(false)

  return (
    <div>
      <Card noPadding>
        <CardBar open={detailsOpen || cardOpen || addDataOpen}>
          <Row align="center" gutterWidth={8} justify="between">
            <Col xs={7}>
              <div>
                <Body color={theme.colors.white}>{terminal.suborganization}</Body>
                <HorizontalSpacer spacing={1} />
                <BodyLarge color={theme.colors.white}>{terminal.name}</BodyLarge>
              </div>
              <HorizontalSpacer spacing={1} />
              <ProgressBar warningMax={TERMINAL_MAX_WARNING} currentProgress={progressPercentage} border />
              <HorizontalSpacer spacing={0.5} />
              <Body textAlign="center" color={theme.colors.white}>
                {mbGbDisplay(terminal.dataUsage?.used ?? 0)} / {mbGbDisplay(terminal.dataUsage?.total ?? 0)}
              </Body>
            </Col>
            <Col xs={5} style={{ display: "flex", justifyContent: "flex-end" }}>
              <AddDataButton title="Add data" onClick={() => setAddDataOpen(!addDataOpen)} />
            </Col>
          </Row>
          <HorizontalSpacer spacing={1} />
          <Row justify="center" onClick={onClickTerminalDetails}>
            {cardOpen ? (
              <ArrowDropUpIcon color={theme.colors.white} />
            ) : (
              <ArrowDropDownIcon color={theme.colors.white} />
            )}
          </Row>
        </CardBar>
        <HorizontalSpacer spacing={2} />
        <Row justify="center">
          <TextButton
            title="Details"
            onClick={() => setDetailsOpen(!detailsOpen)}
            icon={detailsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          />
        </Row>
        <HorizontalSpacer spacing={2} />

        <SlideOutHeight isVisible={detailsOpen}>
          <HorizontalSpacer spacing={2} />
          <BarContentContainer greybg>
            <H3>Serial number:</H3>
            <H4>{terminal.serialNumber}</H4>
          </BarContentContainer>
          <BarContentContainer>
            <H3>Organization / suborganization:</H3>
            <H4>{terminal.suborganization}</H4>
          </BarContentContainer>
          <BarContentContainer greybg>
            <H3>Groups:</H3>
            <H4>{getTerminalGroupsText(terminal?.groups ?? [])}</H4>
          </BarContentContainer>
          <BarContentContainer>
            <H3>Status:</H3>
            <H4>
              <div style={{ display: "flex", alignItems: "center" }}>
                <TerminalStatusCircle status={terminal.status} />
                <Spacer spacing={0.5} />
                <H4>{terminal.status}</H4>
              </div>
            </H4>
          </BarContentContainer>
          <BarContentContainer greybg>
            <H3>Data plan:</H3>
            <H4>{terminal.dataplan}</H4>
          </BarContentContainer>
        </SlideOutHeight>
        <UsageStatisticsSection
          isOpen={cardOpen}
          id={terminal.apiTerminalId}
          name={terminal.name}
          detailsData={terminal}
        />
        <AddDataSection
          isOpen={addDataOpen}
          onClickCancel={() => setAddDataOpen(false)}
          apiPlanId={terminal.apiPlanId}
          dataPlanName={terminal.dataplan}
        />
      </Card>
      <HorizontalSpacer spacing={2} />
    </div>
  )
}

export default TerminalOverviewMobileview
